import { useEffect, useState } from "react";
import { SelectedItem, folder, branch } from "shared/types/Report";
import useFirestore from "shared/hooks/useFirestore";

const useReleaseTable = ( selectedItems:SelectedItem[], folderList:folder[]) => {
    const [loading, setLoading] = useState(false);
    // const {getBranchId} = useGetBranchId();
    const {readFirestoreOneDoc, readFirestoreWithSort} = useFirestore();
    const [selectedFolderList, setSelectedFolderList] = useState<folder[]>([]);
    const [rotations, setRotations] = useState<{ [key: string]: boolean }>({});
    const [isOpenErrorModal, setIsOpenErrorModal] = useState(false);

    useEffect(() => {
        getRootFolderList();
    }, [selectedItems])

    const onTriClickHandler = (id: string) => {
        setRotations((prevRotations) => ({
            ...prevRotations,
            [id]: !prevRotations[id],
        }));
    };

    const onErrorModalConfirmHandler = () =>{
        setIsOpenErrorModal(false);
    }

    const getRootFolderList = async () => {
        setSelectedFolderList([]);
        const list: folder[] = [];
        const folderSet = new Set<string>(); // 중복 방지를 위한 Set
    
        for (const selectedItem of selectedItems) {
            const base_path = selectedItem.start;
            let folder_id = selectedItem.path.split(base_path)[0] || selectedItem.path.split(base_path)[1];
            folder_id = folder_id.split('/').filter(item => item !== "Report" && item !== "")[0];

            if (!folder_id) {
                continue;
            }

            // 중복 검사 (folder_id + path 조합을 기준으로 확인)
            const folderKey = `${folder_id}_${base_path}`;
            if (folderSet.has(folderKey)) {
                continue;
            }
    
            let sameFolder = false;
            for (const folder of list) {
                if (folder.subFolders && findFolder(folder.subFolders, folder_id)) {
                    sameFolder = true;
                    break;
                }
            }
            if (sameFolder) {
                continue;
            }
    
            const prev_folder = await readFirestoreOneDoc(base_path, folder_id || selectedItem.id).then(res => {
                if (res.exists()) {
                    return res.data();
                }
                return null;
            });
    
            if (prev_folder) {
                const folder: folder = {
                    id: folder_id,
                    path: base_path,
                    name: prev_folder.name,
                    type: prev_folder.type,
                    subFolders: prev_folder.type === "folder" ? [] : undefined,
                };
                const subFolder = await getFolderList(`${folder.path}/${folder.id}/Report`);
    
                // 중복된 하위 폴더 제거
                folder.subFolders = subFolder.filter((sf, index, self) => 
                    self.findIndex(s => s.id === sf.id) === index
                );
    
                list.push(folder);
                folderSet.add(folderKey); // 중복 방지를 위해 추가
            }
        }
    
        // 폴더 이름순 정렬
        const sortedData = list.map(item => {
            if (item.subFolders !== undefined) {
                item.subFolders.sort((a, b) => {
                    // 폴더가 먼저 오도록 정렬
                    if (a.type === "folder" && b.type !== "folder") return -1;
                    if (a.type !== "folder" && b.type === "folder") return 1;
        
                    // 문자열과 숫자 분리 함수
                    const splitName = (name: string) => {
                        const match = name.match(/^([^\d]+)(\d+)$/);
                        if (match) {
                            return {
                                prefix: match[1],
                                number: parseInt(match[2], 10)
                            };
                        }
                        return {
                            prefix: name,
                            number: 0
                        };
                    };
        
                    const aSplit = splitName(a.name);
                    const bSplit = splitName(b.name);
        
                    // 문자열 부분 비교 (오름차순)
                    if (aSplit.prefix !== bSplit.prefix) {
                        return aSplit.prefix.localeCompare(bSplit.prefix);
                    }
        
                    // 문자열이 같으면 숫자 비교 (오름차순)
                    return aSplit.number - bSplit.number;
                });
            }
            return item;
        });
        setSelectedFolderList(sortedData);
    };
    
    
    

    const findFolder = (list:folder[], folder_id:string) => {
        for (const subFolder of list) {
            if (subFolder.id === folder_id) {
                return true; // 폴더를 찾았을 경우 true 반환
            }
            if (subFolder.subFolders) {
                // 하위 폴더가 있을 경우 재귀적으로 탐색
                if (findFolder(subFolder.subFolders, folder_id)) {
                    return true; // 하위 폴더에서 폴더를 찾으면 true 반환
                }
            }
        }
        return false;

    }

    const getFolderList = async(path:string):Promise<folder[]> => {
        const folderList = await readFirestoreWithSort(path, {name:"name", direction:"desc"}).then(res => {
            const list: folder[] = [];
            res.docs.forEach((doc:any) => {
                const folder_id = doc.id;
                const folder_name = doc.data().name;
                const folder_type = doc.data().type;
                list.push({
                    id: folder_id,
                    name: folder_name,
                    type: folder_type,
                    path: path,
                    subFolders: folder_type === 'folder' ? [] : undefined,
                });
            });
            return list;
        });
    
        for (let folder of folderList) {
            if (folder.type === 'folder') {
                folder.subFolders = await getFolderList(`${path}/${folder.id}/Report`);
            }
        }
    
        return folderList;
    }


    const getFolderNameById = (id: string): string => {
        const findFolder = (folders: folder[]): folder | undefined => {
            for (const folder of folders) {
                if (folder.id === id) {
                    return folder;
                }
                if (folder.subFolders) {
                    const subFolder = findFolder(folder.subFolders);
                    if (subFolder) {
                        return subFolder;
                    }
                }
            }
        };

        const folder = findFolder(selectedFolderList);
        return folder ? folder.name : 'Unknown';
    };

    return {
        loading,
        rotations,
        onTriClickHandler,
        isOpenErrorModal,
        onErrorModalConfirmHandler,
        getFolderNameById,
        selectedFolderList
    };
}

export default useReleaseTable;